'use client'

import { MenuIcon } from 'lucide-react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import React, { useState } from 'react'

import { SuitcaseIcon } from './icons/Suitcase'
import { NavSheet } from './NavSheet'
import { Button } from './ui/Button'
import { cn } from './ui/cn'
import { Sheet, SheetTrigger } from './ui/Sheet'
import { Wrapper } from './ui/Wrapper'

interface SiteNavProps {
  className?: string
  logoUrl?: string
  brandName?: string
  isLoggedIn?: boolean
  onLogIn?: () => void
  onLogOut?: () => void
  onSignUp?: () => void
}

const Branding = Wrapper
const Links = Wrapper
const DesktopNav = Wrapper

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, '') || '/'
}

export const SiteNav = ({
  className,
  logoUrl = '/brands/shipplay_dark.svg',
  brandName = 'Ship&Play',
  isLoggedIn = false,
  onLogIn,
  onLogOut,
  onSignUp,
}: SiteNavProps): React.ReactElement => {
  const [isNavSheetOpen, setIsNavSheetOpen] = useState(false)
  const pathname = usePathname()

  const isCurrentPage = (path: string): boolean =>
    normalizePathname(pathname) === normalizePathname(path)

  return (
    <DesktopNav
      className={cn(
        'snplay-glass snplay:sticky snplay:top-0 snplay:z-[80]',
        'snplay:flex snplay:items-center snplay:justify-start',
        className,
      )}
    >
      <nav
        aria-label="Primary"
        className={cn(
          'snplay-bounds snplay:flex snplay:min-h-20 snplay:flex-1',
          'snplay:items-center snplay:justify-between snplay:py-4',
          className,
        )}
      >
        <Branding className="snplay:flex snplay:flex-1 snplay:shrink-0 snplay:items-center">
          <Link
            href="/"
            className={cn(
              'snplay-can-focus snplay:relative snplay:isolate snplay:flex',
              'snplay:items-center snplay:gap-2 snplay:transition',
              // Blur pseudo-element behind the logo so it stays legible when
              // the sticky nav's glass panel sits over dark hero imagery.
              'snplay:before:absolute snplay:before:-inset-4',
              'snplay:before:-z-[1] snplay:before:bg-background',
              "snplay:before:opacity-80 snplay:before:blur-2xl snplay:before:content-['']",
            )}
          >
            <img src={logoUrl} className="snplay:max-h-8 snplay:xs:max-h-10" alt={brandName} />
          </Link>
        </Branding>

        <Links as="ul" className="snplay:flex snplay:items-center">
          <li className="snplay:hidden snplay:lg:block">
            <Link
              href="/"
              className="snplay-main-nav-link snplay-can-focus"
              data-active={isCurrentPage('/booking-widget') || isCurrentPage('/')}
            >
              Home
            </Link>
          </li>
          <li className="snplay:hidden snplay:xl:block">
            <Link
              href="/track-orders"
              className="snplay-main-nav-link snplay-can-focus"
              data-active={isCurrentPage('/track-orders')}
            >
              Shipment Tracking
            </Link>
          </li>
          <li className="snplay:hidden snplay:md:block">
            <Link
              href="/how-it-works"
              className="snplay-main-nav-link snplay-can-focus"
              data-active={isCurrentPage('/how-it-works')}
            >
              How It Works
            </Link>
          </li>
          <li className="snplay:hidden snplay:xl:block">
            <Link
              href="/travel"
              className="snplay-main-nav-link snplay-can-focus"
              data-active={isCurrentPage('/travel')}
            >
              Working with an Advisor?
            </Link>
          </li>
          <li className="snplay:hidden snplay:md:block">
            <Link
              href="/cruises"
              className="snplay-main-nav-link snplay-can-focus"
              data-active={isCurrentPage('/cruises')}
            >
              Ship to a Cruise
            </Link>
          </li>
          <li className="snplay:hidden snplay:xs:block snplay:mr-3">
            <Button variant="brand" href="/book/ship">
              Ship Now
              <SuitcaseIcon className="snplay:size-5" />
            </Button>
          </li>
          <li>
            <Sheet open={isNavSheetOpen} onOpenChange={setIsNavSheetOpen}>
              <SheetTrigger asChild aria-label="Open Nav">
                <Button variant="default">
                  <MenuIcon className="snplay:size-5" />
                  <span className="snplay:sr-only">Open Nav</span>
                </Button>
              </SheetTrigger>
              <NavSheet
                logoUrl={logoUrl}
                brandName={brandName}
                isLoggedIn={isLoggedIn}
                onLogIn={onLogIn}
                onLogOut={onLogOut}
                onSignUp={onSignUp}
                onRequestClose={() => setIsNavSheetOpen(false)}
              />
            </Sheet>
          </li>
        </Links>
      </nav>
    </DesktopNav>
  )
}
